import React, { useEffect, useMemo, useState } from 'react';
import { Dialog } from '@mui/material';
import { useAppSelector } from 'app/hooks';
import { NoteDto, notesApi } from 'services/camMirrorService';
import { getUserId } from 'utils/sessionStorage';
import {
  formatModalDate,
  GmDeleteDialog,
  IconClose,
  IconNote,
  IconPencil,
  IconPlus,
  IconSearch,
  IconTrash,
} from './gmModal';

const MAX = 10000;

const NotesModal: React.FC<{
  open: boolean;
  onClose: () => void;
  studentId: string | number;
  studentFirstName: string;
  viewerRole: 'counselor' | 'student';
}> = ({ open, onClose, studentId, viewerRole }) => {
  const viewerFirstName = useAppSelector((store) => store.basicInformation.myBasicInformation?.first_name);
  const [items, setItems] = useState<NoteDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState('');
  const [pendingDelete, setPendingDelete] = useState<NoteDto | null>(null);
  const isCounselor = viewerRole === 'counselor';

  useEffect(() => {
    const timer = window.setTimeout(() => setFilter(query.trim().toLowerCase()), 150);
    return () => window.clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setEditingId(null);
    notesApi
      .list(studentId, null)
      .then((page) => {
        if (!cancelled) setItems(page.items);
      })
      .catch(() => {
        if (!cancelled) setItems([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, studentId]);

  const visible = useMemo(() => {
    const matched = filter ? items.filter((note) => note.content.toLowerCase().includes(filter)) : items;
    return [...matched].sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
  }, [items, filter]);

  const author = viewerFirstName || 'you';
  const counterOn = draft.length >= 8000;
  const myUserId = getUserId();

  // A counselor only edits counselor notes. A student only edits their own.
  const canChange = (note: NoteDto) => note.createdByRole === viewerRole;

  const authorLabel = (note: NoteDto) => {
    return myUserId && String(note.createdById) === String(myUserId) ? author : 'another counselor';
  };

  const addNote = async () => {
    const content = draft.trim();
    if (!content || saving) return;
    setSaving(true);
    try {
      const created = await notesApi.create(studentId, content);
      setItems((prev) => [created, ...prev]);
      setDraft('');
    } finally {
      setSaving(false);
    }
  };

  const saveEdit = async (note: NoteDto) => {
    const content = editDraft.trim();
    if (!content) return;
    const updated = await notesApi.update(note.externalId, content, note.updatedAt);
    setItems((prev) => prev.map((item) => (item.externalId === note.externalId ? { ...item, ...updated, content: updated?.content || item.content } : item)));
    setEditingId(null);
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    const target = pendingDelete;
    setPendingDelete(null);
    await notesApi.remove(target.externalId);
    setItems((prev) => prev.filter((item) => item.externalId !== target.externalId));
  };

  // Student notes stay on the student's account. Counselors keep their own notes.
  // The shared checklist is the to-do list, not this modal.
  let title = 'Your Notes';
  let subtitle = 'Only you can see these notes.';
  let emptyCopy = 'No notes yet. Add a note to keep track of ideas and reminders.';
  if (isCounselor) {
    title = 'Notes for myself';
    subtitle = 'Not shared with the student or parent.';
    emptyCopy = 'No notes yet. Add a note for myself.';
  }
  const placeholder = isCounselor ? 'Add a note for myself' : 'Add a note';

  return (
    <>
      <Dialog open={open} onClose={onClose} PaperProps={{ className: 'gm-modal' }}>
        <div className="gm-modal-head">
          <div className="gm-modal-head-icon">
            <IconNote />
          </div>
          <div className="gm-modal-head-text">
            <div className="gm-modal-title">
              {title}
              <span className="gm-modal-title-count">({items.length})</span>
            </div>
            <div className="gm-modal-subtitle">{subtitle}</div>
          </div>
          <button type="button" className="gm-modal-close" aria-label="Close notes" onClick={onClose}>
            <IconClose />
          </button>
        </div>
        <div className="gm-modal-body">
          <div className="gm-search-bar">
            <span className="gm-search-icon">
              <IconSearch />
            </span>
            <input
              placeholder={viewerRole === 'counselor' ? 'Search notes...' : 'Search your notes...'}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
            />
          </div>
          <div className="gm-notes-add">
                <textarea
                  placeholder={placeholder}
                  value={draft}
                  onChange={(event) => setDraft(event.target.value.slice(0, MAX))}
                />
                {counterOn && (
                  <div className="gm-char-count" style={{ color: draft.length >= 9500 ? '#b45309' : undefined }}>
                    {draft.length}/{MAX}
                  </div>
                )}
              </div>
              <button type="button" className="gm-btn-primary full" onClick={addNote} disabled={!draft.trim() || saving}>
                <IconPlus />
                {saving ? 'Saving…' : 'Add Note'}
              </button>
          <div className="gm-list-label">
            <span className="gm-list-label-title">Recent notes</span>
            <span className="gm-list-label-count">
              {visible.length} {visible.length === 1 ? 'note' : 'notes'}
            </span>
          </div>
          {loading ? (
            <div className="gm-modal-empty">Loading notes…</div>
          ) : visible.length === 0 ? (
            <div className="gm-modal-empty">{filter ? 'No notes match that search.' : emptyCopy}</div>
          ) : (
            visible.map((note) => (
              <div key={note.externalId} className="gm-item-card">
                {editingId === note.externalId ? (
                  <div className="gm-item-body gm-item-edit">
                    <textarea value={editDraft} onChange={(event) => setEditDraft(event.target.value.slice(0, MAX))} />
                    <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                      <button type="button" className="gm-btn-primary" onClick={() => saveEdit(note)} disabled={!editDraft.trim()}>
                        Save
                      </button>
                      <button type="button" className="gm-btn-secondary" onClick={() => setEditingId(null)}>
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="gm-item-body">
                      <div className="gm-item-title">{note.content}</div>
                      <div className="gm-item-meta">
                        Added {formatModalDate(note.createdAt)} by {authorLabel(note)}
                        {note.lastEditedById ? ` · Edited ${formatModalDate(note.updatedAt)}` : ''}
                      </div>
                    </div>
                    {canChange(note) && (
                      <div className="gm-item-actions">
                        <button
                          type="button"
                          className="gm-icon-btn"
                          aria-label="Edit note"
                          onClick={() => {
                            setEditingId(note.externalId);
                            setEditDraft(note.content);
                          }}
                        >
                          <IconPencil />
                        </button>
                        <button type="button" className="gm-icon-btn" aria-label="Delete note" onClick={() => setPendingDelete(note)}>
                          <IconTrash />
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            ))
          )}
        </div>
      </Dialog>
      <GmDeleteDialog
        open={Boolean(pendingDelete)}
        title="Delete this note?"
        onCancel={() => setPendingDelete(null)}
        onConfirm={confirmDelete}
      />
    </>
  );
};

export default NotesModal;
